import React from 'react';
import {
  TableCellsIcon,
  FunnelIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

export const SmsLogsReviewPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <TableCellsIcon className="w-8 h-8 text-[#FF5722]" />
              <h1 className="text-3xl font-bold text-gray-900">
                SMS Logs Review
              </h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl">
              Understand how to use the SMS Logs page filters, real-time view and export tools to
              monitor message delivery across your account or company.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 1
            </h2>
            <p className="text-base font-medium text-gray-900">
              Review 24‑hour SMS statistics
            </p>
            <p className="mt-2 text-sm text-gray-600">
              See total, delivered, failed, pending and delivery rate for the last 24 hours.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 2
            </h2>
            <p className="text-base font-medium text-gray-900">
              Apply filters to narrow logs
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Filter by status, phone, sender ID, user, message body and date/time.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
              Step 3
            </h2>
            <p className="text-base font-medium text-gray-900">
              Review & download filtered logs
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Preview results, watch the live table and export to CSV, Excel or PDF.
            </p>
          </div>
        </div>

        {/* Stats section explanation */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <ChartBarIcon className="w-7 h-7 text-[#FF5722]" />
            <h3 className="text-lg font-semibold text-gray-900">
              1. 24‑Hour Statistics
            </h3>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            At the top of the <span className="font-medium">SMS Logs</span> page you&apos;ll see a
            row of five cards. These are loaded from the backend using the stats endpoint and
            updated when filters change:
          </p>
          <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
            <li>
              <span className="font-medium">Total</span>: Total number of SMS records in the last 24
              hours for the current user or selected user (if a user filter is applied).
            </li>
            <li>
              <span className="font-medium">Delivered</span>: Messages that have a final{' '}
              <span className="font-mono">delivered</span> status.
            </li>
            <li>
              <span className="font-medium">Failed</span>: Messages that ended in a{' '}
              <span className="font-mono">failed</span> status.
            </li>
            <li>
              <span className="font-medium">Pending</span>: Messages still queued or awaiting a final
              delivery status.
            </li>
            <li>
              <span className="font-medium">Delivery Rate</span>: The percentage of total messages
              that were delivered successfully.
            </li>
          </ul>
          <p className="mt-4 text-xs text-gray-500">
            These stats are recalculated using the backend <span className="font-mono">fetchSmsLogsStats</span>{' '}
            service and respect the selected user filter where applicable.
          </p>
        </div>

        {/* Filters explanation */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <FunnelIcon className="w-7 h-7 text-[#FF5722]" />
            <h3 className="text-lg font-semibold text-gray-900">
              2. Filters and what each one does
            </h3>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            The filter panel controls which logs are loaded from the backend (
            <span className="font-mono">fetchSmsLogs</span>) and shown in both the Review Preview
            and Real‑Time table.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Core filters</h4>
              <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
                <li>
                  <span className="font-medium">Status</span>: Limit logs to a single status (
                  <span className="font-mono">delivered</span>,{' '}
                  <span className="font-mono">failed</span>,{' '}
                  <span className="font-mono">queued</span> (pending), or{' '}
                  <span className="font-mono">sent</span>). Use &quot;All&quot; to see everything.
                </li>
                <li>
                  <span className="font-medium">Phone</span>: Search by recipient phone number. This
                  is useful for troubleshooting a specific customer.
                </li>
                <li>
                  <span className="font-medium">Sender ID</span>: Filter by Sender ID to see how a
                  particular brand or shortcode is performing.
                </li>
                <li>
                  <span className="font-medium">Search Body</span>: Search within message content
                  (e.g. &quot;promo&quot; or &quot;OTP&quot;).
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">User & date/time filters</h4>
              <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
                <li>
                  <span className="font-medium">User</span> (Admins/Super Admins only):
                  <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                    <li>
                      Admins can select from &quot;All Users&quot; or a list of company users loaded
                      from the backend.
                    </li>
                    <li>
                      Super admins have a search‑driven dropdown that queries all users via{' '}
                      <span className="font-mono">fetchAllUsers</span> as they type (by name, email
                      or company).
                    </li>
                  </ul>
                </li>
                <li>
                  <span className="font-medium">Start Date / End Date</span>: Restrict logs to a date
                  range, using the timestamp fields returned by the API.
                </li>
                <li>
                  <span className="font-medium">Start Time / End Time</span>: Further refine within a
                  day for time‑of‑day analysis.
                </li>
                <li>
                  <span className="font-medium">Per Page</span>: Choose how many logs to show per
                  page in the live table (10, 25, 50, 100).
                </li>
              </ul>
            </div>
          </div>
          <p className="mt-4 text-xs text-gray-500">
            Whenever you change a filter, the component updates its internal{' '}
            <span className="font-mono">filters</span> state and calls{' '}
            <span className="font-mono">fetchSmsLogs</span> with those parameters to retrieve a
            fresh page of data.
          </p>
        </div>

        {/* Auto refresh and intervals */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <ArrowPathIcon className="w-7 h-7 text-[#FF5722]" />
            <h3 className="text-lg font-semibold text-gray-900">
              3. Auto Refresh and Real‑Time Updates
            </h3>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            Below the filters you&apos;ll find controls for real‑time monitoring:
          </p>
          <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
            <li>
              <span className="font-medium">Auto Refresh</span> toggle:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>
                  When enabled, the page periodically calls{' '}
                  <span className="font-mono">fetchSmsLogsRealTime</span> with the last updated
                  timestamp and current filters.
                </li>
                <li>
                  New or updated log entries are merged into the live table, sorted by most recent
                  activity.
                </li>
              </ul>
            </li>
            <li>
              <span className="font-medium">Interval</span>: Choose how often the page should poll
              the backend (5s, 10s, 30s, 1m). Shorter intervals give more live behavior; longer
              intervals reduce load.
            </li>
            <li>
              The component also refreshes the 24‑hour statistics when new activity is detected so
              your overview stays current.
            </li>
          </ul>
        </div>

        {/* Review Filter and Download Logs */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <ArrowDownTrayIcon className="w-7 h-7 text-[#FF5722]" />
            <h3 className="text-lg font-semibold text-gray-900">
              4. Review filtered results and download logs
            </h3>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            To double‑check your filters before exporting, use the{' '}
            <span className="font-medium">Review Filter</span> and download controls:
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>
              Click <span className="font-medium">Review Filter</span> to open a preview panel. The
              app calls <span className="font-mono">fetchSmsLogs</span> with your current filters and
              shows a paginated table of matching records.
            </li>
            <li>
              Use <span className="font-medium">Previous</span> and{' '}
              <span className="font-medium">Next</span> in the preview footer to move between pages
              of filtered results without leaving the main SMS Logs view.
            </li>
            <li>
              To download, click <span className="font-medium">Download Logs</span> and choose a
              format:
              <ul className="list-disc list-inside ml-5 mt-1 text-gray-600">
                <li>
                  <span className="font-medium">CSV</span>: Uses the backend helper{' '}
                  <span className="font-mono">downloadLogsAsCSV</span> to generate a CSV file
                  readable by Excel and other tools.
                </li>
                <li>
                  <span className="font-medium">Excel</span>: Builds a CSV under the hood but serves
                  it with an <span className="font-mono">.xlsx</span> filename and Excel MIME type
                  for easy opening in Excel.
                </li>
                <li>
                  <span className="font-medium">PDF</span>: Renders an HTML table of logs in a new
                  browser window and triggers <span className="font-medium">Print</span>, allowing
                  you to save it as a PDF from your browser.
                </li>
              </ul>
            </li>
            <li>
              The export helpers internally call <span className="font-mono">fetchSmsLogs</span>{' '}
              across multiple pages (up to a sane row limit) to build a consolidated dataset that
              matches your current filter configuration.
            </li>
            <li>
              Use <span className="font-medium">Reset Filter</span> at any time to clear all filters
              and return to the default 24‑hour, all‑status, all‑users view.
            </li>
          </ol>
          <p className="mt-4 text-xs text-gray-500">
            Downloads are useful for audit trails, compliance reporting and deeper offline analysis
            of delivery, failure and provider error messages.
          </p>
        </div>

        {/* Live table explanation */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">5. Reading the live SMS Logs table</h3>
          <p className="text-sm text-gray-700 mb-3">
            The main table at the bottom of the page shows the logs that match your filters and are
            kept up to date in near real‑time:
          </p>
          <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
            <li>
              <span className="font-medium">Status</span>: Color‑coded badge showing{' '}
              <span className="font-mono">delivered</span>, <span className="font-mono">failed</span>,{' '}
              <span className="font-mono">queued</span> (pending) or <span className="font-mono">sent</span>.
            </li>
            <li>
              <span className="font-medium">Phone</span> and <span className="font-medium">Sender ID</span>:
              Identify who the SMS went to and which Sender ID was used.
            </li>
            <li>
              <span className="font-medium">Message</span>: A truncated view of the message body, with
              the full text available via the tooltip when you hover.
            </li>
            <li>
              <span className="font-medium">Sent At</span> and <span className="font-medium">Updated At</span>:
              Show when the message was created and last updated (for example, when delivery status
              changed).
            </li>
            <li>
              The <span className="font-medium">Copy ID</span> action copies the internal message ID
              to your clipboard so you can reference it in support tickets or backend queries.
            </li>
          </ul>
          <p className="mt-4 text-xs text-gray-500">
            Use pagination at the bottom right of the table to navigate between pages of logs; the
            &quot;Per Page&quot; filter determines how many rows you see at once.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SmsLogsReviewPage;


